import React, { useState, useEffect } from "react";
import { X, Sparkles, ArrowLeft, ArrowRight, Check, Send, Clock, MessageCircle } from "lucide-react";
import { BRAND, SERVICES, PRICING, defaultDuration } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { createBooking, getSlots, getSettings } from "../lib/api";

const TIMINGS = ["Immediately", "In 3 Days", "Within a Week", "Not Sure"];
const priceNum = (p) => {
  const n = parseInt(String(p).replace(/[^0-9]/g, ""), 10);
  return isNaN(n) ? 0 : n;
};
const money = (n) => "₹" + Number(n).toLocaleString("en-IN");

export default function ServiceSelectModal({ open, therapyIndex, onClose }) {
  const { toast } = useToast();
  const [step, setStep] = useState(0); // 0 choose therapy, 1 sub-items, 2 details
  const [idx, setIdx] = useState(therapyIndex);
  const [selected, setSelected] = useState({}); // key -> {name, price, num, sub}
  const [form, setForm] = useState({ name: "", phone: "", timing: "", message: "", date: "", time: "" });
  const [busy, setBusy] = useState(false);
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [subDur, setSubDur] = useState({});
  const [done, setDone] = useState(null); // {name, svc, whenText, waUrl}

  useEffect(() => {
    if (open) getSettings().then((s) => setSubDur(s.subDurations || {})).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setForm({ name: "", phone: "", timing: "", message: "", date: "", time: "" });
    setSlots(null);
    setDone(null);
    if (therapyIndex === null || therapyIndex === undefined) {
      setIdx(null);
      setStep(0);
    } else {
      setIdx(therapyIndex);
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, therapyIndex]);

  if (!open) return null;

  const therapy = idx !== null && idx !== undefined ? PRICING[idx] : null;
  const selList = Object.values(selected);
  const total = selList.reduce((s, it) => s + it.num, 0);

  const toggle = (sub, name, price) => {
    const key = `${sub}__${name}`;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { name, price, num: priceNum(price), sub };
      if (form.date) {
        const dur = Object.values(next).reduce((s, it) => {
          const k = `${serviceName}|${it.sub}|${it.name}`;
          return s + (subDur[k] != null ? subDur[k] : defaultDuration(serviceName, it.sub, it.name));
        }, 0) || 60;
        setTimeout(() => loadSlots(form.date, dur), 0);
      }
      return next;
    });
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const serviceName = therapy ? therapy.cat : (idx !== null && idx !== undefined ? PRICING[idx]?.cat : "General");

  const durationOf = (it) => {
    const key = `${serviceName}|${it.sub}|${it.name}`;
    return (subDur[key] != null) ? subDur[key] : defaultDuration(serviceName, it.sub, it.name);
  };
  const totalDuration = selList.reduce((s, it) => s + durationOf(it), 0) || 60;

  const loadSlots = (date, dur) => {
    if (!date || !serviceName || serviceName === "General") { setSlots(null); return; }
    setSlotsLoading(true);
    setForm((f) => ({ ...f, time: "" }));
    getSlots(serviceName, date, dur || totalDuration)
      .then((res) => setSlots(res))
      .catch(() => setSlots(null))
      .finally(() => setSlotsLoading(false));
  };

  const submit = async () => {
    if (!form.name || !form.phone) {
      toast({ title: "Please add your name & phone" });
      return;
    }
    setBusy(true);
    const itemsText = selList.map((it) => `${it.name} (${it.price})`).join(", ");
    const svc = serviceName;
    const whenText = form.date ? `${form.date}${form.time ? " at " + form.time : ""}` : (form.timing || "-");
    const message = `${selList.length ? "Selected: " + itemsText + " | Est. total " + money(total) : ""}${form.message ? " | Note: " + form.message : ""}`.trim();
    const duration = totalDuration;
    try {
      await createBooking({ name: form.name, phone: form.phone, service: svc, timing: form.timing || (form.date ? "Scheduled" : "Not Sure"), message, amount: total, date: form.date, time: form.time, duration });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Booking save failed, continuing to WhatsApp:", e);
    }
    // One-tap WhatsApp to the studio - lands in the owner's WhatsApp as an instant new-booking alert
    const waLines = [
      `Hi ${BRAND.name}! New appointment request - waiting for confirmation.`,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Service: ${svc}`,
      selList.length ? `Items: ${itemsText}` : null,
      selList.length ? `Estimated total: ${money(total)}` : null,
      `When: ${whenText}`,
      form.message ? `Note: ${form.message}` : null,
    ].filter(Boolean);
    const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(waLines.join("\n"))}`;
    window.open(waUrl, "_blank");
    setDone({ name: form.name.trim(), svc, whenText, waUrl });
    setBusy(false);
  };

  const inputCls =
    "w-full bg-viimii border hairline rounded-xl px-4 py-3.5 text-cream placeholder-cream/40 focus:outline-none focus:border-gold transition-colors";

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-viimii-2 border hairline rounded-[26px] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b hairline">
          <div className="flex items-center gap-3">
            {!done && ((therapyIndex === null || therapyIndex === undefined) ? step > 0 : step > 1) && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="w-9 h-9 rounded-full grid place-items-center border hairline text-cream/70 hover:text-gold"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div>
              <p className="section-line text-gold flex items-center gap-2"><Sparkles size={12} /> Book a Service</p>
              <h3 className="font-display text-xl text-cream mt-0.5">
                {done ? "Request received" : (
                  <>
                    {step === 0 && "Choose a service"}
                    {step === 1 && (therapy ? therapy.cat : "Select items")}
                    {step === 2 && "Your details"}
                  </>
                )}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="text-cream/60 hover:text-cream"><X size={22} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Waiting-for-confirmation screen after booking */}
          {done && (
            <div className="text-center py-6" data-testid="booking-waiting-screen">
              <div className="mx-auto w-16 h-16 rounded-full grid place-items-center mb-5" style={{ background: "rgba(176,134,63,0.15)" }}>
                <Clock size={28} style={{ color: "var(--viimii-gold)" }} />
              </div>
              <p className="section-line text-gold mb-2">Request Received</p>
              <h4 className="font-display text-2xl text-cream mb-3">Waiting for confirmation</h4>
              <p className="text-sm text-cream/65 max-w-sm mx-auto leading-relaxed">
                Hi {done.name}! Your appointment request at VIIMII STUDIO is received - we're confirming your slot and will update you shortly on WhatsApp.
              </p>
              <div className="mt-5 p-4 rounded-2xl border hairline bg-viimii max-w-sm mx-auto text-left">
                <p className="text-xs text-cream/50 mb-1">Your request</p>
                <p className="text-sm text-cream/85">{done.svc}</p>
                <p className="text-sm text-cream/60 mt-0.5">When: {done.whenText}</p>
              </div>
              <a
                href={done.waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-resend-whatsapp"
                className="mt-5 inline-flex items-center gap-2 text-white text-sm font-medium px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
                style={{ background: "#25D366" }}
              >
                <MessageCircle size={16} /> Resend on WhatsApp
              </a>
              <p className="text-[11px] text-cream/40 mt-3">WhatsApp should have opened - just tap send so our team gets your request instantly.</p>
              <button onClick={onClose} data-testid="booking-done-button" className="mt-4 text-sm text-cream/55 hover:text-gold underline underline-offset-4">
                Done
              </button>
            </div>
          )}

          {/* Step 0: choose therapy */}
          {!done && step === 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => { setIdx(i); setStep(1); }}
                  className="flex items-center gap-4 p-3 rounded-2xl border hairline bg-viimii hover:border-gold transition-colors text-left group"
                >
                  <img src={s.img} alt={s.title} className="w-16 h-16 rounded-xl object-cover" />
                  <div>
                    <div className="font-display text-lg text-cream group-hover:text-gold transition-colors">{PRICING[i].cat}</div>
                    <div className="text-xs text-cream/55">from {s.price}</div>
                  </div>
                  <ArrowRight size={16} className="ml-auto text-cream/40 group-hover:text-gold" />
                </button>
              ))}
            </div>
          )}

          {/* Step 1: sub-items */}
          {!done && step === 1 && therapy && (
            <div className="space-y-6">
              {therapy.groups.map((g) => (
                <div key={g.sub}>
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="text-[13px] tracking-[0.14em] uppercase font-medium" style={{ color: "var(--viimii-clay)" }}>{g.sub}</span>
                    <span className="flex-1 h-px" style={{ background: "rgba(74,58,44,0.12)" }} />
                  </div>
                  {g.desc && <p className="text-xs text-cream/50 italic mb-3">{g.desc}</p>}
                  <div className="space-y-2">
                    {g.items.map(([name, price]) => {
                      const key = `${g.sub}__${name}`;
                      const active = !!selected[key];
                      return (
                        <button
                          key={name}
                          onClick={() => toggle(g.sub, name, price)}
                          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-colors ${active ? "border-transparent" : "hairline hover:border-gold"}`}
                          style={active ? { background: "rgba(176,134,63,0.14)", borderColor: "var(--viimii-gold)" } : {}}
                        >
                          <span className={`w-5 h-5 rounded-md grid place-items-center border ${active ? "border-transparent" : "hairline"}`} style={active ? { background: "var(--viimii-gold)" } : {}}>
                            {active && <Check size={13} className="text-white" />}
                          </span>
                          <span className="text-sm text-cream/85 flex-1">{name}</span>
                          <span className="font-display text-gold-soft" style={{ color: "var(--viimii-gold)" }}>{price}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {therapy.note && <p className="text-xs text-cream/45">* {therapy.note}</p>}
            </div>
          )}

          {/* Step 2: details */}
          {!done && step === 2 && (
            <div className="space-y-4">
              {selList.length > 0 && (
                <div className="p-4 rounded-2xl border hairline bg-viimii">
                  <p className="text-xs text-cream/50 mb-2">Your selection</p>
                  <ul className="space-y-1.5">
                    {selList.map((it) => (
                      <li key={it.name} className="flex justify-between text-sm">
                        <span className="text-cream/80">{it.name}</span>
                        <span className="text-cream/60">{it.price}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between mt-3 pt-3 border-t hairline">
                    <span className="text-cream font-medium">Estimated total</span>
                    <span className="font-display text-lg" style={{ color: "var(--viimii-gold)" }}>{money(total)}</span>
                  </div>
                </div>
              )}
              <input className={inputCls} placeholder="Full Name" value={form.name} onChange={(e) => upd("name", e.target.value)} />
              <input className={inputCls} placeholder="Phone Number" value={form.phone} onChange={(e) => upd("phone", e.target.value)} />

              {/* Date + available slots */}
              <div>
                <label className="text-xs text-cream/55 block mb-1.5">Pick a date</label>
                <input
                  type="date"
                  className={inputCls}
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.date}
                  onChange={(e) => { upd("date", e.target.value); loadSlots(e.target.value); }}
                />
              </div>

              {form.date && (
                <div>
                  <label className="text-xs text-cream/55 block mb-2">
                    Available slots{selList.length ? ` · ${totalDuration} min appointment` : ""}{slots ? ` · ${slots.staffCount} staff` : ""}
                  </label>
                  {slotsLoading && <p className="text-sm text-cream/50">Loading slots…</p>}
                  {!slotsLoading && slots && (
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-44 overflow-y-auto pr-1">
                      {slots.slots.map((sl) => (
                        <button
                          key={sl.time}
                          type="button"
                          disabled={!sl.available}
                          onClick={() => upd("time", sl.time)}
                          className={`text-xs py-2 rounded-lg border transition-colors ${
                            form.time === sl.time ? "text-black border-transparent" :
                            sl.available ? "text-cream/80 hairline hover:border-gold" : "text-cream/25 hairline line-through cursor-not-allowed"
                          }`}
                          style={form.time === sl.time ? { background: "var(--viimii-gold)" } : {}}
                          title={sl.available ? `${sl.left} slot(s) left` : "Full"}
                        >
                          {sl.time}
                        </button>
                      ))}
                    </div>
                  )}
                  {!slotsLoading && slots && slots.slots.every((x) => !x.available) && (
                    <p className="text-xs text-rose mt-2" style={{ color: "var(--viimii-rose)" }}>No slots available on this date - please pick another day.</p>
                  )}
                  {!slotsLoading && !slots && <p className="text-xs text-cream/45">Select the service first to see live availability.</p>}
                </div>
              )}

              {!form.date && (
                <select className={inputCls} value={form.timing} onChange={(e) => upd("timing", e.target.value)}>
                  <option value="">Or choose a rough timing</option>
                  {TIMINGS.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              )}
              <textarea className={inputCls} rows={2} placeholder="Any note for us? (optional)" value={form.message} onChange={(e) => upd("message", e.target.value)} />
            </div>
          )}
        </div>

        {/* Footer */}
        {!done && step >= 1 && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t hairline bg-viimii">
            <div>
              <span className="text-xs text-cream/50">{selList.length} selected</span>
              <div className="font-display text-lg text-cream">{money(total)} <span className="text-xs text-cream/45 font-body">est.</span></div>
            </div>
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 text-black font-medium px-6 py-3.5 rounded-xl hover:opacity-90 transition-opacity"
                style={{ background: "var(--viimii-gold)" }}
              >
                Continue <ArrowRight size={17} />
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={submit}
                data-testid="booking-confirm-button"
                className="inline-flex items-center gap-2 text-black font-medium px-6 py-3.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ background: "var(--viimii-gold)" }}
              >
                <Send size={16} /> {busy ? "Booking..." : "Confirm Booking"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
